import { useEffect, useRef } from 'react';
import { LocationData, Stats, Upgrades, DebugInfo, PowerGrid } from '../types';
import { calculatePowerCosts, calculateCoolingCosts, calculateMaintenanceCosts, getLocationCoolingPowerKW } from '../utils/costCalculations';
import { RENEWABLE_TYPES, SOLAR_PROFILE, WIND_PROFILE, LOCATION_RENEWABLE_FACTORS } from '../constants';

interface GameLoopProps {
  gameTime: number;
  setGameTime: React.Dispatch<React.SetStateAction<number>>;
  isPaused: boolean;
  gameSpeed: number;
  money: number;
  setMoney: React.Dispatch<React.SetStateAction<number>>;
  stats: Stats;
  setRecentEarnings: React.Dispatch<React.SetStateAction<number>>;
  setShowEarningsAnimation: React.Dispatch<React.SetStateAction<boolean>>;
  setDebugInfo: React.Dispatch<React.SetStateAction<DebugInfo>>;
  checkForRandomEvents: () => void;
  processActiveEvents: () => { revenueModifier: number };
  checkForFailures: (currentGameTime: number) => void;
  updateSatisfactionAndAging: (currentGameTime: number, activeEvents?: any[]) => void;
  tickUtilization: (gameTime: number) => void;
  activeEvents: any[];
  dailyStaffCost: number;
  bandwidthPenaltyPerDay: number;
  transitCostPerDay: number;
  ixpRevenueBonusPerDay: number;
  locations: Record<string, LocationData>;
  setLocations: React.Dispatch<React.SetStateAction<Record<string, LocationData>>>;
  powerGrids: Record<string, PowerGrid>;
  setOperationalCosts: React.Dispatch<React.SetStateAction<{ power: number, cooling: number, maintenance: number, staff: number }>>;
  setReputation: React.Dispatch<React.SetStateAction<number>>;
  contractBonusPerDay: number; // kept for API compat — contract revenue now in stats.totalRevenue
  loanCostPerDay: number;
  renewableMaintenanceCostPerDay: number;
  backboneCostPerDay: number;
  backboneSavingsPerDay: number;
  greenCertRevenueBonusPerDay: number;
  upgrades: { powerEfficiency: number; coolingEfficiency: number; businessDevelopment: number; [key: string]: number };
}

export const useGameLoop = (props: GameLoopProps) => {
  // Read the live props on every tick via a ref so the interval never closes
  // over stale values. Only gameSpeed/isPaused belong in the effect deps (they
  // change the timer itself); everything else — locations, powerGrids, upgrades
  // and all the per-day cost/revenue memos — is read fresh from propsRef.current
  // inside the tick. Previously these were captured once and only refreshed when
  // an unrelated dep (stats) happened to change, so dailyNet could accrue with
  // stale transit/bandwidth/loan values.
  const propsRef = useRef<GameLoopProps>(props);
  propsRef.current = props;

  const { gameTime, gameSpeed, isPaused } = props;

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const gameTimeRef = useRef<number>(gameTime);

  // Main Game Loop
  useEffect(() => {
    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    
    // Don't start the interval if the game is paused
    if (isPaused) {
      return;
    }
    
    // Set up the game loop interval
    intervalRef.current = setInterval(() => {
      // Pull the latest props each tick — avoids stale-closure bugs where
      // per-day cost/revenue values or locations were captured once at effect
      // setup and never refreshed.
      const {
        setGameTime, checkForRandomEvents, checkForFailures, updateSatisfactionAndAging,
        activeEvents, tickUtilization, processActiveEvents, locations, powerGrids,
        setLocations, setMoney, upgrades, setOperationalCosts, dailyStaffCost, stats,
        ixpRevenueBonusPerDay, contractBonusPerDay, backboneSavingsPerDay,
        greenCertRevenueBonusPerDay, bandwidthPenaltyPerDay, transitCostPerDay,
        backboneCostPerDay, renewableMaintenanceCostPerDay, loanCostPerDay,
        setReputation, setDebugInfo, setRecentEarnings, setShowEarningsAnimation,
      } = propsRef.current;

      // Update game time
      setGameTime(prevTime => {
        const newTime = prevTime + 1;
        gameTimeRef.current = newTime;
        
        // Check for random events once per in-game day
        if (newTime % 1440 === 0) {
          checkForRandomEvents();
        }
        // Hardware failures and satisfaction checked every 10 ticks
        if (newTime % 10 === 0) {
          checkForFailures(newTime);
          updateSatisfactionAndAging(newTime, activeEvents);
        }
        // Gentle utilization random walk every 60 ticks (~every in-game hour)
        if (newTime % 60 === 0) {
          tickUtilization(newTime);
        }

        return newTime;
      });
      
      // Process active events and get revenue modifier
      const { revenueModifier } = processActiveEvents();
      
      // Derive simulated current hour (0-23). Assumes ~60 ticks per in-game hour.
      const currentHour = Math.floor((gameTimeRef.current / 60) % 24);

      // All revenue and cost values are in $/day. 1 in-game day = 1440 ticks.
      const TICKS_PER_DAY = 1440;

      // ── Renewable + Battery logic (runs every hour tick) ──────────────────
      // Compute per-location: current renewable output, battery charge/discharge,
      // export surplus to grid. Battery chargeLevel updated in setLocations below.
      let exportRevenueThisTick = 0;
      const renewableOffsetKW: Record<string, number> = {};        // applied to IT/power bill
      const renewableCoolingOffsetKW: Record<string, number> = {}; // leftover applied to cooling bill

      if (gameTimeRef.current % 60 === 0) {
        const locUpdates: Record<string, Partial<LocationData>> = {};

        Object.entries(locations).forEach(([locId, loc]) => {
          if (!loc.unlocked) return;

          // 1. Current renewable output this hour (kW)
          let rawRenewableKW = 0;
          const locFactors = LOCATION_RENEWABLE_FACTORS[locId] ?? { solar: 1, wind: 1 };
          (loc.renewableInstallations ?? []).forEach(inst => {
            const rt = RENEWABLE_TYPES.find(r => r.id === inst.sourceType);
            if (!rt) return;
            const profile = inst.sourceType === 'solar' ? SOLAR_PROFILE : WIND_PROFILE;
            const factor = inst.sourceType === 'solar' ? locFactors.solar : locFactors.wind;
            rawRenewableKW += rt.capacityKW * inst.units * profile[currentHour] * factor;
          });

          // 2. Facility power draw (kW) = IT load + utilisation-scaled cooling
          let itDrawKW = 0;
          Object.values(loc.floors ?? {}).forEach(floor => {
            floor.racks?.forEach(rack => {
              rack.servers?.forEach(s => { itDrawKW += s.powerDraw; });
            });
          });
          const coolingDrawKW = getLocationCoolingPowerKW(loc, locId);
          const dcDrawKW = itDrawKW + coolingDrawKW;

          if (rawRenewableKW === 0) return;

          // 3. Determine surplus / deficit against the whole facility
          const surplus = Math.max(0, rawRenewableKW - dcDrawKW); // more than facility needs
          const deficit  = Math.max(0, dcDrawKW - rawRenewableKW); // facility needs more than renewable

          const bat = loc.batteryStorage?.installed ? loc.batteryStorage : null;
          const chargeRateKWh = bat ? (bat.chargeRateKW ?? 500) : 0; // kWh chargeable per hour (500 kW default for older saves)
          let newCharge = bat?.chargeLevel ?? 0;
          let batteryDischargeKWh = 0;
          let batteryChargedThisHour = 0;

          if (bat) {
            if (surplus > 0) {
              // Surplus → charge battery first
              const canCharge = Math.min(surplus, chargeRateKWh, bat.capacityKWh - newCharge);
              newCharge += canCharge;
              batteryChargedThisHour = canCharge;
            } else if (deficit > 0) {
              // Deficit → discharge battery to cover it
              const canDischarge = Math.min(deficit, chargeRateKWh, newCharge);
              newCharge -= canDischarge;
              batteryDischargeKWh = canDischarge;
            }
          }

          // 4. Export: surplus not absorbed by battery (or no battery)
          const absorbedByBattery = batteryChargedThisHour;
          const exportKWh = Math.max(0, surplus - absorbedByBattery);

          if (exportKWh > 0 && loc.selectedPowerSupplierId) {
            const grid = powerGrids[loc.powerGridId ?? ''];
            const supplier = grid?.suppliers.find((s: any) => s.id === loc.selectedPowerSupplierId);
            if (supplier) {
              const feedInFraction = bat?.exportFeedInFraction ?? 0.6;
              // Resolve current rate for this hour
              let currentRate = supplier.costPerKWh;
              const schedule = (supplier as any).tariffSchedule;
              if (schedule) {
                for (const win of schedule) {
                  const inWindow = win.startHour < win.endHour
                    ? currentHour >= win.startHour && currentHour < win.endHour
                    : currentHour >= win.startHour || currentHour < win.endHour;
                  if (inWindow) { currentRate = win.rate; break; }
                }
              }
              exportRevenueThisTick += exportKWh * currentRate * feedInFraction;
            }
          }

          // 5. Renewable offset for this hour = renewable used directly + battery discharge.
          // Capped at facility draw, then split: IT/power bucket first, leftover to cooling.
          const directCoverKW = Math.min(rawRenewableKW, dcDrawKW);
          const offsetTotal = directCoverKW + batteryDischargeKWh;
          const powerOffset = Math.min(offsetTotal, itDrawKW);
          renewableOffsetKW[locId] = powerOffset;
          renewableCoolingOffsetKW[locId] = Math.max(0, offsetTotal - powerOffset);

          // 6. Persist updated battery charge level
          if (bat && newCharge !== bat.chargeLevel) {
            locUpdates[locId] = { batteryStorage: { ...bat, chargeLevel: newCharge } };
          }
        });

        if (Object.keys(locUpdates).length > 0) {
          setLocations(prev => {
            const next = { ...prev };
            Object.entries(locUpdates).forEach(([id, patch]) => {
              next[id] = { ...next[id], ...patch };
            });
            return next;
          });
        }

        if (exportRevenueThisTick > 0) {
          setMoney(m => m + exportRevenueThisTick);
        }
      } else {
        // Non-hourly ticks: use the last-known renewable output for cost offset
        // Approximate: average output at current hour (good enough between hourly updates)
        Object.entries(locations).forEach(([locId, loc]) => {
          if (!loc.unlocked) return;
          let rawRenewableKW = 0;
          const locFactors2 = LOCATION_RENEWABLE_FACTORS[locId] ?? { solar: 1, wind: 1 };
          (loc.renewableInstallations ?? []).forEach(inst => {
            const rt = RENEWABLE_TYPES.find(r => r.id === inst.sourceType);
            if (!rt) return;
            const profile = inst.sourceType === 'solar' ? SOLAR_PROFILE : WIND_PROFILE;
            const factor = inst.sourceType === 'solar' ? locFactors2.solar : locFactors2.wind;
            rawRenewableKW += rt.capacityKW * inst.units * profile[currentHour] * factor;
          });
          if (rawRenewableKW === 0) return;

          let itDrawKW = 0;
          Object.values(loc.floors ?? {}).forEach(floor => {
            floor.racks?.forEach(rack => {
              rack.servers?.forEach(s => { itDrawKW += s.powerDraw; });
            });
          });
          const coolingDrawKW = getLocationCoolingPowerKW(loc, locId);
          const dcDrawKW = itDrawKW + coolingDrawKW;

          // Battery discharges when deficit — use current chargeLevel as proxy
          const bat = loc.batteryStorage?.installed ? loc.batteryStorage : null;
          let batteryDischargeKW = 0;
          if (bat && bat.chargeLevel > 0 && rawRenewableKW < dcDrawKW) {
            const deficit = dcDrawKW - rawRenewableKW;
            batteryDischargeKW = Math.min(deficit, bat.chargeRateKW ?? 500, bat.chargeLevel);
          }

          const directCoverKW = Math.min(rawRenewableKW, dcDrawKW);
          const offsetTotal = directCoverKW + batteryDischargeKW;
          const powerOffset = Math.min(offsetTotal, itDrawKW);
          renewableOffsetKW[locId] = powerOffset;
          renewableCoolingOffsetKW[locId] = Math.max(0, offsetTotal - powerOffset);
        });
      }
      // ─────────────────────────────────────────────────────────────────────

      const dailyPowerCost = calculatePowerCosts(locations, powerGrids, currentHour, renewableOffsetKW, upgrades);
      const dailyCoolingCost = calculateCoolingCosts(locations, powerGrids, renewableCoolingOffsetKW, upgrades);
      const dailyMaintenanceCost = calculateMaintenanceCosts(locations);

      setOperationalCosts(prev => ({
        ...prev,
        power: dailyPowerCost,
        cooling: dailyCoolingCost,
        maintenance: dailyMaintenanceCost,
        staff: dailyStaffCost,
      }));

      // Net daily income → applied per tick so money accrues at the correct rate
      const baseRevenue = stats.totalRevenue;
      // Renewable savings are now implicit: dailyPowerCost uses actual gridDrawKW
      // (renewable output and battery discharge reduce grid draw → lower power bill).
      // Export revenue is accrued directly to money every hour tick above.
      const dailyNet =
        baseRevenue
        + revenueModifier
        + ixpRevenueBonusPerDay
        + contractBonusPerDay
        + backboneSavingsPerDay
        + greenCertRevenueBonusPerDay
        - bandwidthPenaltyPerDay
        - transitCostPerDay
        - backboneCostPerDay
        - dailyPowerCost
        - dailyCoolingCost
        - dailyMaintenanceCost
        - dailyStaffCost
        - renewableMaintenanceCostPerDay
        - loanCostPerDay;

      const netPerTick = dailyNet / TICKS_PER_DAY;

      // Passive reputation recovery toward natural ceiling of 100.
      // Business Development speeds recovery (+20% per level).
      // Events can push past 100 (up to 110); values above 100 decay back to 100.
      if (gameTimeRef.current % TICKS_PER_DAY === 0) {
        const recovery = 0.5 * (1 + 0.2 * (upgrades.businessDevelopment || 0));
        setReputation(prev => {
          if (prev < 100) return Math.min(100, prev + recovery);
          if (prev > 100) return Math.max(100, prev - 0.5);
          return prev;
        });
      }

      setMoney(prevMoney => {
        const newMoney = prevMoney + netPerTick;

        setDebugInfo(prev => ({
          ...prev,
          lastRevenueCheck: Date.now(),
          totalRevenue: stats.totalRevenue,
          lastEarningsAmount: dailyNet,
          previousMoney: prevMoney,
          calculatedNewMoney: newMoney,
        }));

        if (dailyNet > 0) {
          setRecentEarnings(netPerTick);
          setShowEarningsAnimation(true);
          setTimeout(() => setShowEarningsAnimation(false), 1500);
        }

        return newMoney;
      });
      
      // Generator fuel cost — deduct running cost per tick while generator is active.
      // Fuel level is managed by App.tsx (drains 1 hour per 60 ticks during outage).
      Object.entries(locations).forEach(([_locId, loc]) => {
        if (loc.generator?.installed && loc.generator.isRunning && loc.generator.fuelLevelHours > 0) {
          const fuelBurnPerTick = 1 / 3600; // matches App.tsx outage drain rate (1hr / 3600 ticks per day)
          const fuelCostThisTick = loc.generator.fuelCostPerHour * fuelBurnPerTick;
          setMoney(prev => prev - fuelCostThisTick);
        }
      });

      // Update debug info
      setDebugInfo((prev: DebugInfo) => ({
        ...prev,
        lastGameTick: Date.now(),
        lastMoneyUpdate: Date.now(),
        lastEarningsAmount: stats.totalRevenue
      }));
      
    }, 250 / gameSpeed); // 250ms base = 6 real min per in-game day at 1×
    
    // Clean up the interval when the component unmounts or dependencies change
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
    // Only the timer-controlling props are deps; all data is read from
    // propsRef.current inside the tick (see comment at top of the hook).
  }, [gameSpeed, isPaused]);

  return {
    // Return any values or functions that might be needed outside
    intervalRef
  };
};
